import { insertAllAdvertBanners } from "@modules/azm/advert-banners.ts"
import { replaceBreadcrumb } from "@modules/breadcrumb/breadcrumb"
import {
    updateAdvertCardContainerUI,
    updateAdvertResultsFoundInfoInDom,
} from "@modules/global/advert-card-container.ts"
import { topStarsStripeChangeVisibility } from "@modules/stars/top-stars-stripe"
import { hideTopAdverts, showTopAdverts } from "@modules/top-adverts/top-adverts"
import type { ApiFiltersResult } from "@modules/types"
import { isPC } from "@modules/util/device.ts"
import { dispatchGlobalEvent } from "@modules/util/events"
import { updateLinks } from "@modules/util/update-links"
import { updateUrl } from "@modules/util/url"
import { advertCardsContainerElement } from "../global/dom.ts"
import { addMerkzettelIconToAdvertCards } from "./advert-card-merkzettel-highlighting"
import { hideResultsTopBar, showResultsTopBar } from "./advert-card-view-type"
import { updateAffiliateAdvertsInDom } from "./affiliates"
import { isActiveStarsFilter } from "./filter"
import { updateActiveFilterBarInDom } from "./filter-selection/filter-bar"
import { setShowResultsButton } from "./filter-selection/set-show-results-button"
import { getNextPage, setPagination, updatePaginationButtons, updatePaginationSelect } from "./pagination/pagination"
import { resetAdvertsForUserAndNotify } from "./snapshot-updated-notification"

export function removeSamePageContent() {
    if (isPC()) {
        const container = advertCardsContainerElement()
        if (container) {
            container.innerHTML = ""
        }
        return
    }
    const pageStartElement = advertCardsContainerElement()?.querySelector(`#page-section-${getNextPage() - 1}`)
    let nextElement = pageStartElement?.nextElementSibling
    while (continueRemovingSamePageElement(nextElement)) {
        const oldEl = nextElement
        nextElement = nextElement?.nextElementSibling
        oldEl?.remove()
    }
    pageStartElement?.remove()
}

export function continueRemovingSamePageElement(el: Element | null | undefined) {
    if (!el) {
        return false
    }
    return !el.id.includes("page-section")
}

// TODO via callbacks or event based.
export async function afterFilterApiFetched(
    filterApiResult: ApiFiltersResult | undefined,
    options: { insertAdjacentHTML?: InsertPosition; removeSamePageContent?: boolean } = {},
) {
    if (!filterApiResult) {
        return
    }
    dispatchGlobalEvent("azm.filter-api.after-fetch", filterApiResult)
    const advertCardsContainerEl = advertCardsContainerElement()
    if (advertCardsContainerEl) {
        await new Promise((resolve) =>
            requestAnimationFrame(() => {
                if (options.removeSamePageContent) {
                    removeSamePageContent()
                }
                if (options.insertAdjacentHTML) {
                    advertCardsContainerEl.insertAdjacentHTML(options.insertAdjacentHTML, filterApiResult.content)
                } else {
                    advertCardsContainerEl.innerHTML = filterApiResult.content
                }
                resolve(null)
            }),
        )
    }

    updateAdvertCardContainerUI(filterApiResult.advertCount)
    replaceBreadcrumb(filterApiResult.breadcrumb)
    updateUrl(filterApiResult.seoUrl)
    setPagination(filterApiResult.pagination)
    setShowResultsButton(filterApiResult.advertCount || 0)
    updatePaginationButtons(filterApiResult.pagination)
    updatePaginationSelect(filterApiResult.pagination)
    addMerkzettelIconToAdvertCards()
    topStarsStripeChangeVisibility(!isActiveStarsFilter())
    updateActiveFilterBarInDom()
    updateAdvertResultsFoundInfoInDom(filterApiResult)
    updateAffiliateAdvertsInDom()
    updateLinks()
    insertAllAdvertBanners(true)

    if (filterApiResult.advertCount > 0) {
        showResultsTopBar()
        showTopAdverts()
    } else {
        hideResultsTopBar()
        hideTopAdverts()
    }

    SiteInfo.endsWithLastAdvertInGroup = filterApiResult.endsWithLastAdvertInGroup || false
    SiteInfo.currentSearchHash = filterApiResult.currentSearchHash || ""

    if (filterApiResult.snapshotUpdatedDueToNewDay || filterApiResult.snapshotUpdatedDueToNewIndex) {
        resetAdvertsForUserAndNotify()
    }
}
